import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { DateTimeService } from '@shared/services/DateTimeService';
import { StarRatingStatic } from '../../StarRating';
import { displayDistanceFromCenter, closeOpenedPopup, getPopupContent } from '../helpers';
import {
  FONT_FAMILY_MAIN,
  LIGHT_BLUE_GREY,
  LINKS_COLOR,
  NOTES_COLOR,
  PALE_GREY,
  TEXT_COLOR,
} from '../../../theme';
import iconCalendar from '../../../images/icon-calendar.svg';
import media from '../../../utils/mediaQueries';
import { ExamCentersLogoService } from '../../../services/ExamCentersLogoService';

const Item = styled.li`
  border-radius: 12px;
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.05);
  background-color: #ffffff;
  overflow: hidden;

  & + & {
    margin-top: 24px;
  }
`;

const Body = styled.a`
  display: flex;
  color: ${TEXT_COLOR};

  &:hover,
  &:focus,
  &:active {
    text-decoration: none;
    color: ${TEXT_COLOR};
  }

  ${media.mobile`
    flex-direction: column;
  `}
`;

const Info = styled.div`
  flex: 1;
  padding: 16px 16px 16px 32px;
  font-family: ${FONT_FAMILY_MAIN};
  color: ${TEXT_COLOR};

  ${media.mobile`
    order: 2;
    padding: 16px;
  `}
`;

const ImgContainer = styled.div`
  width: 30%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px 0;

  ${media.mobile`
    order: 1;
    border-left: none;
    justify-content: flex-start;
    width: 100%;
    height: 72px;
  `}

  img {
    width: 70%;
    height: auto;
    max-width: 72px;
    border-radius: 6px;
  }
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.13px;
  margin: 0 0 8px;
`;

const Address = styled.div`
  font-size: 14px;
  line-height: 1.29;
  letter-spacing: -0.13px;
  color: ${NOTES_COLOR};
  margin-bottom: 16px;
`;

const ReviewsCount = styled.span`
  color: ${LINKS_COLOR};
  margin-left: 8px;

  ${media.mobile`
    display: block;
    margin-left: 0;
    margin-top: 8px;
  `}
`;

const ReservationInfo = styled.div`
  margin-top: 16px;
  font-size: 10px;
  line-height: 1.4;
  letter-spacing: -0.2px;
  color: ${LIGHT_BLUE_GREY};
`;

const NextSession = styled.div`
  color: ${LINKS_COLOR};
  font-size: 11px;
  letter-spacing: -0.1px;
  padding: 3px 6px 4px 20px;
  background-color: #f4f8fc;
  position: relative;
  display: inline-block;
  border-radius: 7px;

  &::before {
    content: '';
    width: 10px;
    height: 9px;
    background-image: url(${iconCalendar});
    background-size: contain;
    position: absolute;
    left: 6px;
    top: 5px;
  }
`;

const Rating = styled.div`
  line-height: 16px;
`;

const FooterInfo = styled.a`
  border-top: 1px solid ${PALE_GREY};
  display: flex;

  &:hover,
  &:focus {
    text-decoration: none;
  }

  ${media.mobile`
    flex-direction: column;
    background: ${LINKS_COLOR};
    align-items: center;
  `}
`;

const FooterBtn = styled.div`
  background: ${LINKS_COLOR};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
  padding: 16px;
  display: block;
  width: 55%;

  ${media.mobile`
    width: 100%;
    padding: 10px 16px 0;
  `}
`;

const BookingsAmount = styled.div`
  font-size: 12px;
  line-height: 1.33;
  letter-spacing: normal;
  text-align: center;
  color: #979ea7;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45%;
  white-space: pre-wrap;

  ${media.mobile`
    width: 50%;
    font-size: 10px;
    line-height: 1.2;
    color: #dae8f9;
    padding-bottom: 12px;
    white-space: normal;
  `}
`;

class ExamCentersListItem extends Component {
  handleHover = () => {
    const { isMobile } = this.props;
    if (!isMobile) {
      this.createPopUp();
    }
  };

  createPopUp = () => {
    closeOpenedPopup();
    const { data, mapObject } = this.props;
    const { properties } = data;
    const popup_content = getPopupContent(properties);

    new mapboxgl.Popup({ offset: 25, className: 'vv-exam-map__popup' })
      .setLngLat(data.geometry.coordinates)
      .setHTML(popup_content)
      .addTo(mapObject);
  };

  render() {
    const { data } = this.props;
    const { properties } = data;
    const {
      id,
      title,
      adress,
      zipcode,
      city,
      distance_from_center,
      avg_ratings_score,
      ratings_count,
      next_session,
      http_path,
      is_codengo,
      is_la_poste,
      is_dekra,
      is_sgs,
      meaningful_reservations_count,
    } = properties;

    return (
      <Item data-row-id={id} onMouseEnter={this.handleHover}>
        <Body href={`${http_path}`} target="_blank" rel="noopener noreferrer">
          <Info>
            <Title>{title}</Title>
            <Address>
              {`${adress} ${zipcode} ${city}`}
              {displayDistanceFromCenter(distance_from_center)}
            </Address>
            <Rating>
              <StarRatingStatic
                starsAmount={5}
                width={17}
                height={16}
                activeStarIdx={Math.round(avg_ratings_score)}
              />
              <ReviewsCount>{`${ratings_count} avis`}</ReviewsCount>
            </Rating>
            <ReservationInfo>
              {next_session ? (
                <NextSession>{`Prochaine session le ${DateTimeService.setDateLocale(
                  new Date(next_session),
                  "d MMM 'à' H:mm"
                )}`}</NextSession>
              ) : (
                'Réservation indisponible'
              )}
            </ReservationInfo>
          </Info>
          <ImgContainer>{ExamCentersLogoService.getLogo(properties.type)}</ImgContainer>
        </Body>
        {is_codengo || is_la_poste || is_dekra || is_sgs ? (
          <FooterInfo href={`${http_path}#reservation`} target="_blank" rel="noopener noreferrer">
            <FooterBtn>Je réserve ma session d&apos;examen</FooterBtn>
            <BookingsAmount>
              {`${meaningful_reservations_count} élèves ont déjà réservé\nleur examen sur ce centre`}
            </BookingsAmount>
          </FooterInfo>
        ) : null}
      </Item>
    );
  }
}

ExamCentersListItem.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string,
    geometry: PropTypes.shape({
      type: PropTypes.string,
      coordinates: PropTypes.arrayOf(PropTypes.number),
    }),
    properties: PropTypes.object,
  }).isRequired,
  mapObject: PropTypes.object,
  isMobile: PropTypes.bool,
};

ExamCentersListItem.defaultProps = {
  isMobile: false,
  mapObject: null,
};

export { ExamCentersListItem };
